import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Zap, Star } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL
  ? `${process.env.REACT_APP_BACKEND_URL}/api`
  : 'https://devisfast-backend.onrender.com/api';

export default function ReviewPage() {
  const [rating, setRating] = useState(5);
  const [hover, setHover] = useState(0);
  const [name, setName] = useState('');
  const [job, setJob] = useState('');
  const [text, setText] = useState('');
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!name.trim()) return setError('Votre prénom et nom sont requis.');
    if (!job.trim()) return setError('Votre métier est requis.');
    if (text.trim().length < 10) return setError('Votre témoignage est trop court.');
    if (!consent) return setError('Vous devez accepter que votre avis soit publié.');

    setLoading(true);
    try {
      await axios.post(`${API}/reviews`, { name, job, rating, text, consent });
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Une erreur est survenue. Réessayez.');
    }
    setLoading(false);
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] text-white flex items-center justify-center px-4" style={{fontFamily:"'DM Sans', sans-serif"}}>
        <div className="max-w-md w-full text-center">
          <div className="text-6xl mb-6">⭐</div>
          <h1 className="text-3xl font-bold mb-4" style={{fontFamily:"'Syne', sans-serif"}}>Merci pour votre avis !</h1>
          <p className="text-gray-400 mb-8">Votre témoignage va aider d'autres artisans à découvrir DevisFast. Il sera publié après validation.</p>
          <Link to="/">
            <Button className="bg-[#2563EB] hover:bg-[#1d4ed8] text-white rounded-full px-8 py-4">
              Retour à l'accueil
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white" style={{fontFamily:"'DM Sans', sans-serif"}}>

      {/* Header */}
      <header className="border-b border-white/5 px-4 py-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-[#2563EB] rounded-xl flex items-center justify-center">
              <Zap className="w-4 h-4 text-white"/>
            </div>
            <span className="text-lg font-bold" style={{fontFamily:"'Syne', sans-serif"}}>DEVISFAST</span>
          </Link>
          <Link to="/" className="text-gray-400 hover:text-white text-sm transition-colors">← Retour à l'accueil</Link>
        </div>
      </header>

      {/* Form */}
      <div className="max-w-lg mx-auto px-4 py-16">
        <div className="text-center mb-10">
          <h1 className="text-3xl md:text-4xl font-bold mb-3" style={{fontFamily:"'Syne', sans-serif"}}>
            Donnez votre avis
          </h1>
          <p className="text-gray-400">Votre expérience aide d'autres artisans à choisir DevisFast.</p>
        </div>

        <form onSubmit={handleSubmit} className="bg-[#1a1a24] rounded-2xl p-8 border border-white/5">

          {/* Étoiles */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-300 mb-3">Votre note</label>
            <div className="flex gap-2">
              {[1,2,3,4,5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => setRating(star)}
                  onMouseEnter={() => setHover(star)}
                  onMouseLeave={() => setHover(0)}
                  className="text-3xl transition-transform hover:scale-110"
                >
                  <span style={{color: star <= (hover || rating) ? '#f59e0b' : '#374151'}}>★</span>
                </button>
              ))}
            </div>
          </div>

          {/* Nom */}
          <div className="mb-5">
            <label className="block text-sm font-medium text-gray-300 mb-2">Votre prénom et nom</label>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="Jean Dupont"
              className="w-full bg-[#0f0f17] border border-white/10 rounded-xl px-4 py-3 text-white placeholder-gray-600 focus:outline-none focus:border-[#2563EB] transition-colors"
            />
          </div>

          {/* Métier */}
          <div className="mb-5">
            <label className="block text-sm font-medium text-gray-300 mb-2">Votre métier</label>
            <input
              type="text"
              value={job}
              onChange={e => setJob(e.target.value)}
              placeholder="Plombier, Jardinier, Peintre..."
              className="w-full bg-[#0f0f17] border border-white/10 rounded-xl px-4 py-3 text-white placeholder-gray-600 focus:outline-none focus:border-[#2563EB] transition-colors"
            />
          </div>

          {/* Témoignage */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-300 mb-2">Votre témoignage</label>
            <textarea
              value={text}
              onChange={e => setText(e.target.value)}
              placeholder="Décrivez votre expérience avec DevisFast..."
              rows={4}
              className="w-full bg-[#0f0f17] border border-white/10 rounded-xl px-4 py-3 text-white placeholder-gray-600 focus:outline-none focus:border-[#2563EB] transition-colors resize-none"
            />
            <div className="text-right text-xs text-gray-600 mt-1">{text.length} caractères</div>
          </div>

          {/* Consentement */}
          <div className="mb-6">
            <label className="flex items-start gap-3 cursor-pointer">
              <div
                onClick={() => setConsent(!consent)}
                className={`w-5 h-5 rounded border flex-shrink-0 mt-0.5 flex items-center justify-center transition-colors cursor-pointer ${consent ? 'bg-[#2563EB] border-[#2563EB]' : 'border-white/20 bg-[#0f0f17]'}`}
              >
                {consent && <span className="text-white text-xs">✓</span>}
              </div>
              <span className="text-sm text-gray-400">
                J'accepte que mon prénom, mon métier et mon avis soient affichés publiquement sur le site DevisFast.
              </span>
            </label>
          </div>

          {/* Erreur */}
          {error && (
            <div className="mb-4 bg-red-500/10 border border-red-500/30 rounded-xl px-4 py-3 text-red-400 text-sm">
              {error}
            </div>
          )}

          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-[#2563EB] hover:bg-[#1d4ed8] text-white rounded-full py-5 text-base font-semibold disabled:opacity-50"
          >
            {loading ? 'Envoi en cours...' : 'Publier mon avis →'}
          </Button>
        </form>
      </div>
    </div>
  );
}
